import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, Calendar, Clock, MapPin, Users, Send, Loader2, Truck, AlertCircle, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { getAttribution } from '../lib/attribution';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Debounce hook for address input
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function EstimateForm({ isOpen, onClose }) {
  const { items, getTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prefilled, setPrefilled] = useState({});
  const [formData, setFormData] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const pre = {};
    // Support multiple param name formats (camelCase, snake_case, GHL contact.* style)
    const firstName = params.get('firstName') || params.get('first_name') || params.get('contact.first_name') || params.get('fname') || '';
    const lastName = params.get('lastName') || params.get('last_name') || params.get('contact.last_name') || params.get('lname') || '';
    const email = params.get('email') || params.get('contact.email') || '';
    const phone = params.get('phone') || params.get('contact.phone') || params.get('contact.phone_raw') || '';
    if (firstName) pre.firstName = true;
    if (lastName) pre.lastName = true;
    if (email) pre.email = true;
    if (phone) pre.phone = true;
    // Store which fields came from URL so we can show a badge
    setTimeout(() => setPrefilled(pre), 0);
    return {
      firstName: decodeURIComponent(firstName),
      lastName: decodeURIComponent(lastName),
      email: decodeURIComponent(email),
      phone: decodeURIComponent(phone).replace(/^\+1/, ''),
      eventDate: '',
      eventTime: '',
      streetAddress: '',
      city: '',
      state: 'FL',
      zipCode: '',
      eventType: '',
      guestCount: '',
      notes: '',
    };
  });

  // Delivery fee state
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [isCalculatingDelivery, setIsCalculatingDelivery] = useState(false);
  const [deliveryError, setDeliveryError] = useState(null);

  // Build full address from components
  const getFullAddress = useCallback(() => {
    const { streetAddress, city, state, zipCode } = formData;
    if (!city || !state) return '';
    const parts = [streetAddress, city, state, zipCode].filter(Boolean);
    return parts.join(', ');
  }, [formData]);

  // Debounce the full address
  const debouncedAddress = useDebounce(getFullAddress(), 800);

  // Calculate delivery fee when address changes
  const calculateDelivery = useCallback(async (address) => {
    if (!address || !formData.city || !formData.state) {
      setDeliveryInfo(null);
      setDeliveryError(null);
      return;
    }

    setIsCalculatingDelivery(true);
    setDeliveryError(null);

    try {
      const response = await axios.post(`${API}/delivery/calculate`, {
        address: address
      });
      setDeliveryInfo(response.data);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Could not calculate delivery fee';
      setDeliveryError(errorMessage);
      setDeliveryInfo(null);
    } finally {
      setIsCalculatingDelivery(false);
    }
  }, [formData.city, formData.state]);

  useEffect(() => {
    if (debouncedAddress) {
      calculateDelivery(debouncedAddress);
    }
  }, [debouncedAddress, calculateDelivery]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Capture lead when contact info is available (fire once per session)
  const [leadCaptured, setLeadCaptured] = useState(false);
  const { firstName, lastName, email, phone } = formData;
  useEffect(() => {
    if (leadCaptured || !isOpen) return;
    if (firstName && lastName && email && phone) {
      setLeadCaptured(true);
      axios.post(`${API}/leads/capture`, {
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
      }).catch((err) => console.error('Lead capture failed (non-blocking)', err));
    }
  }, [isOpen, firstName, lastName, email, phone, leadCaptured]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Calculate grand total with delivery
    const grandTotal = getGrandTotal();
    const fullAddress = getFullAddress();

    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        event_date: formData.eventDate,
        event_time: formData.eventTime,
        event_location: fullAddress,
        event_type: formData.eventType,
        guest_count: parseInt(formData.guestCount) || 0,
        items: items.map(item => ({
          id: item.id,
          name: item.name,
          description: item.description,
          price: item.price,
          quantity: item.quantity,
          hours: item.hours,
          additionalHourPrice: item.additionalHourPrice || 0,
          category: item.category,
          image: item.image,
          quotePending: item.quotePending || false,
        })),
        total: grandTotal,
        delivery_fee: deliveryInfo?.delivery_fee || 0,
        delivery_distance: deliveryInfo?.distance_miles || 0,
        notes: formData.notes,
        payment_percent: paymentInfo?.percent || 100,
        amount_due: paymentInfo?.amountDue || grandTotal,
        attribution: getAttribution(),
      };

      const response = await axios.post(`${API}/estimates`, payload);
      
      const thankYouData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        eventType: formData.eventType,
        eventDate: formData.eventDate,
        eventTime: formData.eventTime,
        eventLocation: formData.eventLocation,
        guestCount: formData.guestCount,
        itemCount: items.length,
        items: items.map(i => ({ name: i.name, price: i.price, quantity: i.quantity, hours: i.hours, additionalHourPrice: i.additionalHourPrice || 0, quotePending: i.quotePending })),
        total: grandTotal,
        deliveryFee: deliveryInfo?.delivery_fee || 0,
        hasQuotePending: items.some(i => i.quotePending),
        paymentUrl: response.data?.payment_url || null,
        proposalId: response.data?.proposal_id || null,
        amountDue: paymentInfo?.amountDue || grandTotal,
        paymentPercent: paymentInfo?.percent || 100,
      };
      
      clearCart();
      onClose();
      navigate('/thank-you', { state: thankYouData });
    } catch (error) {
      // silent
      toast.error('Submission Failed', {
        description: 'Please try again or contact us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: price % 1 === 0 ? 0 : 2,
    }).format(price);
  };

  // Calculate grand total including delivery
  const getGrandTotal = () => {
    const itemsTotal = getTotal();
    const deliveryFee = deliveryInfo?.delivery_fee || 0;
    return itemsTotal + deliveryFee;
  };

  // Payment rule: <=7 days = full payment, >7 days = 50% deposit
  const getPaymentInfo = () => {
    if (!formData.eventDate) return null;
    const eventDate = new Date(formData.eventDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = eventDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const grandTotal = getGrandTotal();
    if (diffDays <= 7) {
      return { fullPayment: true, percent: 100, amountDue: grandTotal, daysOut: diffDays };
    }
    return { fullPayment: false, percent: 50, amountDue: Math.round(grandTotal * 0.5), daysOut: diffDays };
  };

  const paymentInfo = getPaymentInfo();

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 overflow-hidden"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        data-testid="estimate-form-backdrop"
      />

      {/* Modal Container - Centered with padding */}
      <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-4 pointer-events-none">
        {/* Modal */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative w-full max-w-3xl bg-surface border border-white/10 flex flex-col pointer-events-auto rounded-lg sm:rounded-none"
          style={{ maxHeight: 'calc(100vh - 1rem)' }}
          data-testid="estimate-form-modal"
        >
          {/* Header - Fixed */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-white/10 flex-shrink-0">
            <div>
              <h2 className="font-display text-xl sm:text-2xl text-white">Review Your Estimate</h2>
              <p className="text-white/50 text-xs sm:text-sm">Complete your booking request</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/50 hover:text-white transition-colors"
              data-testid="close-estimate-form-btn"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto min-h-0" style={{ maxHeight: 'calc(100vh - 6rem)' }}>
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Order Summary */}
            <div className="bg-background/50 p-3 sm:p-4 border border-white/10 rounded-md">
              <h3 className="text-white font-medium mb-3 sm:mb-4 text-sm sm:text-base">Selected Experiences</h3>
              <div className="space-y-3">
                {items.map((item) => {
                  // Calculate item total with hours
                  const isQuotePending = item.quotePending;
                  let itemTotal = item.price * item.quantity;
                  if (item.additionalHourPrice && item.hours > 1) {
                    itemTotal += item.additionalHourPrice * (item.hours - 1) * item.quantity;
                  }
                  return (
                    <div key={item.id} className="flex justify-between text-sm">
                      <div className="flex items-center gap-2">
                        {item.image && (
                          <img src={item.image} alt={item.name} className="w-8 h-8 object-cover rounded flex-shrink-0" />
                        )}
                        <div className="text-white/70">
                          <span>{item.name}</span>
                          {item.quantity > 1 && <span> x{item.quantity}</span>}
                          {item.hours > 1 && (
                            <span className="text-white/50 ml-1">
                              ({item.hours} hrs @ {formatPrice(item.price)} + {formatPrice(item.additionalHourPrice)}/hr)
                            </span>
                          )}
                          {item.hours === 1 && item.additionalHourPrice > 0 && (
                            <span className="text-white/50 ml-1">(1 hr)</span>
                          )}
                        </div>
                      </div>
                      <span className={`font-medium ${isQuotePending ? 'text-amber-400 text-xs' : 'text-gold'}`}>
                        {isQuotePending ? 'Quote Pending' : formatPrice(itemTotal)}
                      </span>
                    </div>
                  );
                })}
              </div>
              
              {/* Subtotal */}
              <div className="mt-4 pt-4 border-t border-white/10 flex justify-between text-sm">
                <span className="text-white/70">Experiences Subtotal</span>
                <span className="text-white font-medium">{formatPrice(getTotal())}</span>
              </div>
              {items.some(i => i.quotePending) && (
                <p className="text-amber-400/80 text-xs mt-1">+ Items pending quote (not included in total)</p>
              )}

              {/* Delivery Fee Section */}
              <div className="mt-3 flex justify-between items-center text-sm">
                <div className="flex items-center gap-2 text-white/70">
                  <Truck className="w-4 h-4" />
                  <span>Delivery Fee</span>
                  {deliveryInfo && !deliveryInfo.is_custom_quote && (
                    <span className="text-white/40">({deliveryInfo.distance_miles} mi)</span>
                  )}
                </div>
                <div className="text-right">
                  {isCalculatingDelivery ? (
                    <span className="text-white/50 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Calculating...
                    </span>
                  ) : deliveryInfo ? (
                    deliveryInfo.is_custom_quote ? (
                      <span className="text-gold">Custom Quote</span>
                    ) : deliveryInfo.delivery_fee === 0 ? (
                      <span className="text-green-400 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Included
                      </span>
                    ) : (
                      <span className="text-gold font-medium">{formatPrice(deliveryInfo.delivery_fee)}</span>
                    )
                  ) : (
                    <span className="text-white/40">Enter address below</span>
                  )}
                </div>
              </div>

              {/* Delivery Error */}
              {deliveryError && (
                <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-xs">{deliveryError}</p>
                </div>
              )}

              {/* Grand Total */}
              <div className="mt-4 pt-4 border-t border-white/10 flex justify-between">
                <span className="text-white font-medium">Estimated Total</span>
                <span className="font-display text-xl text-gold">
                  {deliveryInfo?.is_custom_quote ? (
                    <span className="text-base">
                      {formatPrice(getTotal())} + Delivery TBD
                    </span>
                  ) : (
                    formatPrice(getGrandTotal())
                  )}
                </span>
              </div>

              {/* Payment Rule Notice */}
              {paymentInfo && !deliveryInfo?.is_custom_quote && (
                <div
                  className={`mt-4 p-3 rounded border bg-gold/10 border-gold/30`}
                  data-testid="payment-rule-notice"
                >
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-gold" />
                    <div>
                      <p className="text-sm font-medium text-gold">
                        {paymentInfo.fullPayment
                          ? 'Full Payment Required'
                          : '50% Deposit Required'}
                      </p>
                      <p className="text-white/50 text-xs mt-1">
                        {paymentInfo.fullPayment
                          ? 'Events within 7 days require full payment upon booking.'
                          : 'Events more than 7 days out require a 50% deposit. Remaining balance due before event date.'}
                      </p>
                      <p className="text-sm font-semibold mt-1 text-gold">
                        Amount Due: {formatPrice(paymentInfo.amountDue)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-white font-medium mb-3 sm:mb-4 text-sm sm:text-base">Contact Information</h3>
              {Object.keys(prefilled).length > 0 && (
                <div className="mb-3 p-2.5 bg-green-500/10 border border-green-500/20 rounded-md flex items-center gap-2" data-testid="prefilled-banner">
                  <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <p className="text-green-400 text-xs">Your contact info has been pre-filled. Feel free to make any changes.</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-white/50 text-sm mb-2">First Name</label>
                  <Input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="bg-background/50 border-white/10 text-white placeholder:text-white/30"
                    placeholder="John"
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <label className="block text-white/50 text-sm mb-2">Last Name</label>
                  <Input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="bg-background/50 border-white/10 text-white placeholder:text-white/30"
                    placeholder="Doe"
                    data-testid="input-last-name"
                  />
                </div>
                <div>
                  <label className="block text-white/50 text-sm mb-2">Email</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-background/50 border-white/10 text-white placeholder:text-white/30"
                    placeholder="john@example.com"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label className="block text-white/50 text-sm mb-2">Phone</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="bg-background/50 border-white/10 text-white placeholder:text-white/30"
                    placeholder="(555) 123-4567"
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div>
              <h3 className="text-white font-medium mb-3 sm:mb-4 text-sm sm:text-base">Event Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-white/50 text-sm mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Event Date
                  </label>
                  <Input
                    type="date"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                    required
                    className="bg-background/50 border-white/10 text-white"
                    data-testid="input-event-date"
                  />
                </div>
                <div>
                  <label className="block text-white/50 text-sm mb-2">
                    <Clock className="w-4 h-4 inline mr-2" />
                    Event Time
                  </label>
                  <Input
                    type="time"
                    name="eventTime"
                    value={formData.eventTime}
                    onChange={handleChange}
                    required
                    className="bg-background/50 border-white/10 text-white"
                    data-testid="input-event-time"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-white/50 text-sm mb-2">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Event Location (for delivery estimate)
                  </label>
                  <Input
                    name="streetAddress"
                    value={formData.streetAddress}
                    onChange={handleChange}
                    className="bg-background/50 border-white/10 text-white placeholder:text-white/30"
                    placeholder="Street Address"
                    data-testid="input-street-address"
                  />
                </div>
                <div>
                  <label className="block text-white/50 text-sm mb-2">City</label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="bg-background/50 border-white/10 text-white placeholder:text-white/30"
                    placeholder="Miami"
                    data-testid="input-city"
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-white/50 text-sm mb-2">State</label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      className="w-full h-10 px-3 bg-background/50 border border-white/10 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-gold text-sm sm:text-base"
                      data-testid="input-state"
                    >
                      <option value="AL">AL</option>
                      <option value="AK">AK</option>
                      <option value="AZ">AZ</option>
                      <option value="AR">AR</option>
                      <option value="CA">CA</option>
                      <option value="CO">CO</option>
                      <option value="CT">CT</option>
                      <option value="DE">DE</option>
                      <option value="FL">FL</option>
                      <option value="GA">GA</option>
                      <option value="HI">HI</option>
                      <option value="ID">ID</option>
                      <option value="IL">IL</option>
                      <option value="IN">IN</option>
                      <option value="IA">IA</option>
                      <option value="KS">KS</option>
                      <option value="KY">KY</option>
                      <option value="LA">LA</option>
                      <option value="ME">ME</option>
                      <option value="MD">MD</option>
                      <option value="MA">MA</option>
                      <option value="MI">MI</option>
                      <option value="MN">MN</option>
                      <option value="MS">MS</option>
                      <option value="MO">MO</option>
                      <option value="MT">MT</option>
                      <option value="NE">NE</option>
                      <option value="NV">NV</option>
                      <option value="NH">NH</option>
                      <option value="NJ">NJ</option>
                      <option value="NM">NM</option>
                      <option value="NY">NY</option>
                      <option value="NC">NC</option>
                      <option value="ND">ND</option>
                      <option value="OH">OH</option>
                      <option value="OK">OK</option>
                      <option value="OR">OR</option>
                      <option value="PA">PA</option>
                      <option value="RI">RI</option>
                      <option value="SC">SC</option>
                      <option value="SD">SD</option>
                      <option value="TN">TN</option>
                      <option value="TX">TX</option>
                      <option value="UT">UT</option>
                      <option value="VT">VT</option>
                      <option value="VA">VA</option>
                      <option value="WA">WA</option>
                      <option value="WV">WV</option>
                      <option value="WI">WI</option>
                      <option value="WY">WY</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/50 text-sm mb-2">Zip Code</label>
                    <Input
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                      required
                      className="bg-background/50 border-white/10 text-white placeholder:text-white/30"
                      placeholder="33101"
                      data-testid="input-zip-code"
                    />
                  </div>
                </div>
                {/* Delivery Status Feedback */}
                <div className="md:col-span-2">
                  {isCalculatingDelivery && (
                    <p className="text-white/40 text-xs flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Calculating delivery from Homestead, FL...
                    </p>
                  )}
                  {deliveryInfo && !deliveryInfo.is_custom_quote && deliveryInfo.delivery_fee === 0 && (
                    <p className="text-green-400 text-xs flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Great news! Delivery is included for your location ({deliveryInfo.distance_miles} miles)
                    </p>
                  )}
                  {deliveryInfo && deliveryInfo.delivery_fee > 0 && !deliveryInfo.is_custom_quote && (
                    <p className="text-gold text-xs flex items-center gap-1">
                      <Truck className="w-3 h-3" />
                      Delivery fee: {formatPrice(deliveryInfo.delivery_fee)} ({deliveryInfo.distance_miles} miles from Homestead, FL)
                    </p>
                  )}
                  {deliveryInfo && deliveryInfo.is_custom_quote && (
                    <p className="text-gold text-xs">
                      Your location is 700+ miles away. We'll provide a custom delivery quote.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-white/50 text-sm mb-2">Event Type</label>
                  <Input
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    required
                    className="bg-background/50 border-white/10 text-white placeholder:text-white/30"
                    placeholder="Birthday, Corporate, Wedding..."
                    data-testid="input-event-type"
                  />
                </div>
                <div>
                  <label className="block text-white/50 text-sm mb-2">
                    <Users className="w-4 h-4 inline mr-2" />
                    Expected Guests
                  </label>
                  <Input
                    type="number"
                    name="guestCount"
                    value={formData.guestCount}
                    onChange={handleChange}
                    required
                    min="1"
                    className="bg-background/50 border-white/10 text-white placeholder:text-white/30"
                    placeholder="50"
                    data-testid="input-guest-count"
                  />
                </div>
              </div>
            </div>

            {/* Additional Notes */}
            <div>
              <label className="block text-white/50 text-sm mb-2">Additional Notes</label>
              <Textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="bg-background/50 border-white/10 text-white placeholder:text-white/30 resize-none text-sm sm:text-base"
                placeholder="Any special requests or requirements..."
                data-testid="input-notes"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 sm:py-4 bg-gold text-background font-medium hover:bg-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base rounded-md sm:rounded-none"
              data-testid="submit-estimate-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Estimate Request
                </>
              )}
            </button>

            <p className="text-white/30 text-xs text-center pb-4">
              By submitting, you agree to be contacted regarding your booking inquiry.
            </p>
          </form>
        </div>
      </motion.div>
      </div>
    </motion.div>
  );
}
